import { Trans } from "@lingui/react/macro";
import { graphql } from "gql.tada";
import { ImportIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";
import { useQuery } from "urql";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { URLGroupInputValue } from "../url-group-input";
import ImportUrlGroupDialog from "./import-url-group-dialog";

// 組織のすべてのSA & UAプロジェクトを取得するクエリ
const GetProjectsQuery = graphql(`
  query GetProjects($workspaceId: ID!) {
    node(id: $workspaceId) {
      id
      __typename
      ... on Organization {
        projects {
          id
          name
          __typename
          ... on SearchAnalyticsProject {
            searchAnalytics {
              id
              urlGroups {
                id
                name
                op
                patterns {
                  type
                  pattern
                }
              }
            }
          }
          ... on UserAnalyticsProject {
            userAnalytics {
              id
              urlGroups {
                id
                name
                op
                patterns {
                  type
                  pattern
                }
              }
            }
          }
        }
      }
    }
  }
`);

type IwImportUrlGroupButtonProps = {
  workspaceId: string;
  onChange: (value: URLGroupInputValue) => void;
};

export function IwImportUrlGroupButton({
  workspaceId,
  onChange,
}: IwImportUrlGroupButtonProps) {
  const [{ data, fetching }] = useQuery({
    query: GetProjectsQuery,
    variables: { workspaceId },
  });

  const saProjects =
    data?.node?.__typename === "Organization"
      ? data.node.projects
          .filter((project) => project.__typename === "SearchAnalyticsProject")
          .map((project) => ({
            id: project.id,
            name: project.name,
            type: "sa" as const,
            urlGroups:
              project.__typename === "SearchAnalyticsProject"
                ? project.searchAnalytics?.urlGroups.map((group) => ({
                    ...group,
                    patterns: group.patterns.filter(
                      (pattern): pattern is NonNullable<typeof pattern> =>
                        pattern !== null,
                    ),
                  })) || []
                : [],
          }))
      : [];

  const uaProjects =
    data?.node?.__typename === "Organization"
      ? data.node.projects
          .filter((project) => project.__typename === "UserAnalyticsProject")
          .map((project) => ({
            id: project.id,
            name: project.name,
            type: "ua" as const,
            urlGroups:
              project.__typename === "UserAnalyticsProject"
                ? project.userAnalytics?.urlGroups?.map((group) => ({
                    ...group,
                    patterns: group.patterns.filter(
                      (pattern): pattern is NonNullable<typeof pattern> =>
                        pattern !== null,
                    ),
                  })) || []
                : [],
          }))
      : [];

  const [dialogMode, setDialogMode] = useState<"sa_to_iw" | "ua_to_iw" | null>(
    null,
  );

  // カスタムハンドラーでURLグループを直接onChangeに渡す
  const handleImport = (urlGroup: {
    op: string;
    patterns: { type: string; pattern: string }[];
  }) => {
    onChange({
      op: urlGroup.op as URLGroupInputValue["op"],
      patterns: urlGroup.patterns.map((p) => ({
        type: p.type as URLGroupInputValue["patterns"][0]["type"],
        pattern: p.pattern,
      })),
    });
  };

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={fetching}
                type="button"
              >
                {fetching ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  <ImportIcon className="w-4 h-4" />
                )}
                <span className="sr-only">
                  <Trans>Import URL Group</Trans>
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <Trans>Import URL Group</Trans>
          </TooltipContent>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => setDialogMode("sa_to_iw")}>
              <Trans>Import from Search Analytics</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDialogMode("ua_to_iw")}>
              <Trans>Import from User Analytics</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </Tooltip>
      </DropdownMenu>

      <ImportUrlGroupDialog
        mode="sa_to_iw"
        sourceProjects={saProjects}
        isOpen={dialogMode === "sa_to_iw"}
        onOpenChange={(open) => {
          if (!open) {
            setDialogMode(null);
          } else {
            setDialogMode("sa_to_iw");
          }
        }}
        onImport={handleImport}
      />

      <ImportUrlGroupDialog
        mode="ua_to_iw"
        sourceProjects={uaProjects}
        isOpen={dialogMode === "ua_to_iw"}
        onOpenChange={(open) => {
          if (!open) {
            setDialogMode(null);
          } else {
            setDialogMode("ua_to_iw");
          }
        }}
        onImport={handleImport}
      />
    </>
  );
}
